import { useEffect, useState } from 'react';

// ── Emoji tile palette (deterministic per table slug) ──
// Light tint + matching border: one shared lightness treatment keeps the
// palette coherent and puts emoji on a light surface at full contrast.

const TILE_HUES = [
  '91, 140, 111',   // sage (brand)
  '77, 170, 170',   // teal
  '59, 130, 246',   // blue
  '155, 126, 200',  // violet
  '217, 119, 6',    // amber
  '236, 112, 140',  // rose
];

const getTileStyle = (slug = '') => {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) & 0xffffffff;
  }
  const rgb = TILE_HUES[Math.abs(hash) % TILE_HUES.length];
  return {
    backgroundColor: `rgba(${rgb}, 0.14)`,
    border: `1px solid rgba(${rgb}, 0.28)`,
  };
};

// Icon art carries its own sage/coral palette, so it sits on a plain white
// tile — the deterministic hue tint above exists to give EMOJI a light ground
// and would fight the artwork. White also guarantees contrast in the table
// detail header, which sits on brand green (the art's sage fills would
// otherwise blend into it).
const ART_TILE_STYLE = {
  backgroundColor: '#FFFFFF',
  border: '1px solid var(--card-border)',
};

// One icon treatment for every render site: the Tables browse card (56px), both
// table detail headers (40px), and the feed's Popular Tables widget (28px).
// Falls back to the emoji when a table has no art or the file 404s, so a newly
// added table still renders.
//
// Note the art is NOT used in the feed's PostCard table badge: that badge is a
// text-sized pill and the art is 144x144 line work drawn for a 56px tile — at
// ~12px it reads as a smudge, which is exactly the size emoji are designed for.
const TableIcon = ({ table, px = 56, radius = 'rounded-xl' }) => {
  const [broken, setBroken] = useState(false);
  const useArt = !!table?.image_url && !broken;

  // Re-arm on a new URL. Without this, once a 404 set `broken` the component
  // kept showing the emoji even after the art was replaced — which is exactly
  // what the admin icon preview does on every upload.
  useEffect(() => { setBroken(false); }, [table?.image_url]);

  return (
    <div
      className={`${radius} flex-shrink-0 flex items-center justify-center overflow-hidden`}
      style={{ width: px, height: px, ...(useArt ? ART_TILE_STYLE : getTileStyle(table?.slug)) }}
    >
      {useArt ? (
        <img
          src={table.image_url}
          alt=""
          loading="lazy"
          onError={() => setBroken(true)}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        <span style={{ fontSize: px * 0.45, lineHeight: 1 }}>{table?.emoji || '💬'}</span>
      )}
    </div>
  );
};

export default TableIcon;
